// Topbar — Curro brand bar + role-based navigation + theme toggle + snapshot
// run-picker (admin) + user chip. Under ~1260px the inline nav becomes a fixed
// bottom tab bar; on phones the theme/run controls move into an options sheet.
//   admin:  Explore · Report · Pipeline · Users
//   client: Explore · Report
//   tenant: Explore · Report   (server force-scopes their data)

import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { AnalyticsApi } from "../hooks/useApi.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { RunPicker } from "../hooks/useRun.jsx";
import Icon from "./Icon.jsx";

const NAV = {
  admin:  [["/", "Explore", "compass"], ["/report", "Report", "report"], ["/pipeline", "Pipeline", "pipeline"], ["/users", "Users", "users"]],
  client: [["/", "Explore", "compass"], ["/report", "Report", "report"]],
  tenant: [["/", "Explore", "compass"], ["/report", "Report", "report"]],
};

function CurroMark() {
  return (
    <span className="mark">
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 7.5A6 6 0 1 0 17 16.5" />
      </svg>
    </span>
  );
}

function ThemeSeg({ theme, setTheme, withLabels = false }) {
  return (
    <div className="seg theme-seg" title="Theme">
      <button type="button" className={theme === "light" ? "on" : ""} onClick={() => setTheme("light")} aria-label="Light theme">
        <Icon name="sun" size={14} />{withLabels ? " Light" : ""}
      </button>
      <button type="button" className={theme === "dark" ? "on" : ""} onClick={() => setTheme("dark")} aria-label="Dark theme">
        <Icon name="moon" size={14} />{withLabels ? " Dark" : ""}
      </button>
    </div>
  );
}

export default function Topbar() {
  const { user, signOut } = useAuth();
  const [status, setStatus] = useState("");
  const [opt, setOpt] = useState(false);
  const [theme, setTheme] = useState(() =>
    localStorage.getItem("pa_theme")
    || (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"));

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("pa_theme", theme);
  }, [theme]);

  useEffect(() => {
    if (!user?.role) return;
    AnalyticsApi.health()
      .then((h) => setStatus(`Ready · ${Number(h.rows).toLocaleString()} lines · snapshot #${h.publishRunId ?? "—"}`))
      .catch(() => setStatus("API unavailable"));
  }, [user]);

  const links = NAV[user?.role] ?? [];
  const initial = (user?.displayName || user?.email || "?")[0].toUpperCase();

  return (
    <>
      <header className="topbar">
        <NavLink className="brand" to="/" title={status}>
          <CurroMark />
          <span className="word">curr<span className="dot">o</span></span>
          <span className="sep" />
          <span className="ctx"><b>Procurement Analytics</b><span>The Media Crowd</span></span>
        </NavLink>

        <nav className="nav desktop-nav">
          {links.map(([to, label, icon]) => (
            <NavLink key={to} to={to} end={to === "/"} className={({ isActive }) => (isActive ? "active" : "")}>
              <Icon name={icon} size={17} /> {label}
            </NavLink>
          ))}
        </nav>

        <div className="top-right">
          <ThemeSeg theme={theme} setTheme={setTheme} />
          <RunPicker />
          <div className="user-chip" title={user?.email}>
            <div className="who"><b>{user?.displayName || user?.email}</b><span>{user?.role}</span></div>
            <span className="av">{initial}</span>
          </div>
          <button type="button" className="opt-btn" aria-label="Options" onClick={() => setOpt(!opt)}>
            <Icon name="settings" size={18} />
          </button>
        </div>
      </header>

      {opt && (
        <>
          <div className="opt-backdrop" onClick={() => setOpt(false)} />
          <div className="opt-sheet">
            <div className="opt-row"><span className="opt-lbl">Theme</span><ThemeSeg theme={theme} setTheme={setTheme} withLabels /></div>
            <div className="opt-row"><span className="opt-lbl">Snapshot</span><RunPicker /></div>
            <div className="opt-row"><span className="opt-lbl">Account</span>
              <button type="button" className="btn ghost sm" onClick={signOut}><Icon name="logout" size={13} /> Sign out</button>
            </div>
            {status && <p className="hint" style={{ margin: 0 }}>{status}</p>}
          </div>
        </>
      )}

      <nav className="tabbar">
        {links.map(([to, label, icon]) => (
          <NavLink key={to} to={to} end={to === "/"} className={({ isActive }) => `tab ${isActive ? "active" : ""}`}>
            <Icon name={icon} size={20} /><span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}
